import axios from 'axios'
import { usePlatformStore } from '@/stores/platformStore'

const API_URL = import.meta.env.VITE_API_URL

// Dedicated client so platform requests carry the platform token (not the
// org-user token). 401s are surfaced to the caller for the console to handle.
const platformClient = axios.create({
  baseURL: API_URL ? `${API_URL}/api/platform` : '/api/platform',
  headers: { 'Content-Type': 'application/json' },
})

platformClient.interceptors.request.use((config) => {
  const token = usePlatformStore.getState().token
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

platformClient.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      usePlatformStore.getState().logout()
      if (!window.location.pathname.endsWith('/login')) window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

export interface PlatformAdminInfo { id: string; name: string; email: string }
export interface FeatureInfo { key: string; label: string }
export interface OrgSummary {
  id: string
  name: string
  slug: string
  is_active: boolean
  user_count: number
  slack_connected: boolean
  disabled_features: string[]
  enabled_features: string[]
  created_at?: string | null
}
export interface OrgUser { id: string; name: string; email: string; role: string; is_active: boolean }
export interface OrgDetail extends OrgSummary { users: OrgUser[] }
export interface PlatformStats {
  total_orgs: number
  active_orgs: number
  inactive_orgs: number
  total_users: number
  active_users: number
}

export const platformApi = {
  me: async () => (await platformClient.get<PlatformAdminInfo>('/me')).data,
  stats: async () => (await platformClient.get<PlatformStats>('/stats')).data,
  features: async () => (await platformClient.get<FeatureInfo[]>('/features')).data,
  listOrgs: async () => (await platformClient.get<OrgSummary[]>('/orgs')).data,
  orgDetail: async (id: string) => (await platformClient.get<OrgDetail>(`/orgs/${id}`)).data,
  setFeatures: async (id: string, disabled_features: string[]) =>
    (await platformClient.patch<OrgSummary>(`/orgs/${id}/features`, { disabled_features })).data,
  deactivate: async (id: string) => (await platformClient.post<OrgSummary>(`/orgs/${id}/deactivate`)).data,
  activate: async (id: string) => (await platformClient.post<OrgSummary>(`/orgs/${id}/activate`)).data,
  deleteOrg: async (id: string, confirm_slug: string) =>
    (await platformClient.delete(`/orgs/${id}`, { data: { confirm_slug } })).data,
}
